import { useEffect, useMemo, useRef, useState, useContext } from 'react'
import FullCalendar, { DateSelectArg, EventClickArg } from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import DocumentTitle from 'react-document-title'
import { PageTitle } from '../../../_metronic/layout/core'
import Select from 'react-select'
import axios from 'axios'
import CalendarEventEditModal from './CalendarEventEditModal'
import { companyOptionsList, companySitesList } from '../charts/core/_requests'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import { CustomizationContext } from '../customization/CustomizationContext'

type CalendarEvent = {
  id: string
  title: string
  start: string
  end?: string
  allDay?: boolean
}

const EventsCalendarPage = () => {
  const calendarRef = useRef<FullCalendar | null>(null)
  // @ts-ignore
  const { settings } = useContext(CustomizationContext)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [editId, setEditId] = useState<string>('') // '' none, 'add' for create, or numeric id
  const [initialEntity, setInitialEntity] = useState<any>(null)
  const [companyOptions, setCompanyOptions] = useState<{ label: string; value: string }[]>([])
  const [siteOptions, setSiteOptions] = useState<{ label: string; value: string }[]>([])
  const [filters, setFilters] = useState<{
    company: { label: string; value: string } | null
    companySite: { label: string; value: string } | null
    eventType: string | null
    status: { label: string; value: string } | null
    startDate: string
    endDate: string
  }>({
    company: null,
    companySite: null,
    eventType: null,
    status: null,
    startDate: '',
    endDate: '',
  })

  const breadcrumbs = useMemo(
    () => [
      { title: 'Events', path: '/events/', isSeparator: false, isActive: false },
      { title: '', path: '', isSeparator: true, isActive: false },
    ],
    []
  )

  useEffect(() => {
    companyOptionsList().then((opts) => {
      setCompanyOptions(opts)
    })
  }, [])

  useEffect(() => {
    if (companyOptions.length && settings?.companyS && !filters.company) {
      const userCompany = companyOptions.find((c) => c.value === settings.companyS)
      if (userCompany) {
        setFilters((p) => ({ ...p, company: userCompany }))
      }
    }
  }, [companyOptions, settings, filters.company])

  useEffect(() => {
    if (filters.company) {
      companySitesList({ companyId: Number(filters.company.value) }).then(setSiteOptions)
    } else {
      setSiteOptions([])
      setFilters((prev) => ({ ...prev, companySite: null }))
    }
  }, [filters.company])

  const fetchEvents = () => {
    if (!filters.company || !filters.companySite) {
      setEvents([])
      return
    }
    const params: any = {
      company: filters.company.value,
    }
    if (filters.companySite) params.company_site = filters.companySite.value
    if (filters.eventType) params.event_type = filters.eventType
    if (filters.status) params.status = filters.status.value
    if (filters.startDate) params.start_date = filters.startDate
    if (filters.endDate) params.end_date = filters.endDate
    axios.get('/events/calendar/data', { params }).then((resp) => {
      const apiEvents = resp.data?.data?.events ?? resp.data?.events ?? []
      const mapped: CalendarEvent[] = apiEvents.map((e: any) => {
        // API provides { id, title, date, startTime, endTime, allDay, ... }
        const date: string | undefined = e.date
        const startTime: string | undefined = e.startTime
        const endTime: string | undefined = e.endTime
        const allDay: boolean = !!e.allDay
        const start = date ? (startTime ? `${date}T${startTime}` : `${date}`) : undefined
        const end = date ? (endTime ? `${date}T${endTime}` : undefined) : undefined
        return {
          id: String(e.id),
          title: e.title,
          start: start as string,
          end: end,
          allDay: allDay,
        }
      })
      setEvents(mapped)
    })
  }

  useEffect(() => {
    fetchEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.company,
    filters.companySite,
    filters.eventType,
    filters.status,
    filters.startDate,
    filters.endDate,
  ])

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    // Unselect and open create modal
    selectInfo.view.calendar.unselect()
    // Pre-fill entity using current filters and selected date/time
    const start = selectInfo.start
    const end = selectInfo.end
    const toDate = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
      ).padStart(2, '0')}`
    const toTime = (d: Date) =>
      `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    const dateStr = toDate(start)
    const startTime = selectInfo.view.type.startsWith('timeGrid') ? toTime(start) : undefined
    const endTime = selectInfo.view.type.startsWith('timeGrid') && end ? toTime(end) : undefined
    setInitialEntity({
      date: dateStr,
      start_time: startTime,
      end_time: endTime,
      company: filters.company ? Number(filters.company.value) : undefined,
      company_site: filters.companySite ? Number(filters.companySite.value) : undefined,
      status: 'created',
      all_day: !selectInfo.view.type.startsWith('timeGrid'),
      frequency: 'one_time',
    })
    setEditId('add')
  }

  const handleEventClick = (clickInfo: EventClickArg) => {
    const e = clickInfo.event
    setEditId(String(e.id))
    setInitialEntity(null)
  }

  return (
    <>
      <DocumentTitle title={process.env.REACT_APP_NAME + ' | Events Calendar'}></DocumentTitle>
      <PageTitle breadcrumbs={breadcrumbs}>Events Calendar</PageTitle>
      <div className='d-flex align-items-center justify-content-between mb-5'>
        <div className='d-flex gap-2'>
          <a className='btn btn-light' href='/events/list'>
            <i className='fa fa-list me-2'></i> List View
          </a>
          <a className='btn btn-light-primary' href='/events/calendar'>
            <i className='fa fa-calendar me-2'></i> Calendar View
          </a>
        </div>
      </div>

      <div className='card mb-5'>
        <div className='card-body'>
          <div className='row g-5'>
            <div className='col-12 col-md-3'>
              <label className='form-label'>Company</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Select company'
                value={filters.company}
                onChange={(opt: any) => setFilters((p) => ({ ...p, company: opt as any }))}
                options={companyOptions}
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label'>Site</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Select site'
                value={filters.companySite}
                onChange={(opt: any) => setFilters((p) => ({ ...p, companySite: opt as any }))}
                options={siteOptions}
                isDisabled={!filters.company}
              />
            </div>
            <div className='col-12 col-md-2'>
              <AutocompleteSelect
                title='Event Type'
                name='event_type'
                onChange={(event: any) => {
                  setFilters((p) => ({ ...p, eventType: event.target.value }))
                }}
                defaultValue={filters.eventType ?? ''}
                autoCompleteTokenUrl='/event_types/autocomplete'
              />
            </div>

            <div className='col-12 col-md-2'>
              <label className='form-label'>Start Date</label>
              <input
                type='date'
                className='form-control'
                value={filters.startDate}
                onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label'>End Date</label>
              <input
                type='date'
                className='form-control'
                value={filters.endDate}
                onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
              />
            </div>
            <div className='col-12 col-md-2'>
              <label className='form-label'>Status</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Select status'
                value={filters.status}
                onChange={(opt: any) => setFilters((p) => ({ ...p, status: opt as any }))}
                options={[
                  { label: 'Created', value: 'created' },
                  { label: 'Completed', value: 'completed' },
                  { label: 'Cancelled', value: 'cancelled' },
                ]}
              />
            </div>
            <div className='col-12 d-flex gap-2'>
              <button className='btn btn-primary' onClick={fetchEvents} disabled={!filters.company}>
                Apply
              </button>
              <button
                className='btn btn-light'
                onClick={() => {
                  setFilters({
                    company: null,
                    companySite: null,
                    eventType: null,
                    status: null,
                    startDate: '',
                    endDate: '',
                  })
                  setEvents([])
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-body p-5'>
          {!filters.company ? (
            <div className='alert alert-primary'>
              Please select a company to display calendar events.
            </div>
          ) : !filters.companySite ? (
            <div className='alert alert-primary'>
              Please select a company site to display calendar events.
            </div>
          ) : (
            <FullCalendar
              ref={calendarRef as any}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView='dayGridMonth'
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={true}
              events={events}
              select={handleDateSelect}
              eventClick={handleEventClick}
              height='auto'
            />
          )}
        </div>
      </div>
      {editId !== '' ? (
        <CalendarEventEditModal
          show={editId !== ''}
          itemIdForUpdate={editId}
          initialEntity={initialEntity ?? undefined}
          onHide={() => {
            setEditId('')
            setInitialEntity(null)
          }}
          refreshFunction={() => {
            fetchEvents()
          }}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default EventsCalendarPage
